import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Modal, TextField, Typography } from "@mui/material";
import { cssProperties } from "../../../utils/commonCssProperties";
import commonActions from "../../../redux/common/actions";
import examScheduleActions from "../../../redux/examSchedule/actions";
import CancelButton from "../../../common/button/cancelButton";
import BluePrimaryButton from "../../../common/button/bluePrimaryButton";
import ButtonLoader from "../../../common/buttonLoader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 640,
  bgcolor: `${cssProperties?.backgroundcolor?.secondary}`,
  padding: "24px",
  borderRadius: "8px",
  overflowY: "auto",
  maxHeight: "calc(100vh - 20px)",
  scrollbarWidth: "thin",
};

const defaultValues = {
  paper_no: 1,
  paperlabel: "",
  papername: "",
  examdate: "",
  reportingtime: "",
  gateclosingtime: "",
  starttime: "",
  endtime: "",
  marks: "",
  duration: "",
};

const toMinutes = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Add / edit one PAPER row — a column of the paper-wise grid, ordered by
 * paper_no. paper_no 0 is the break column: its papername is the break's name
 * and only its start/end times are printed.
 */
export default function PaperRowModal({ courseid }) {
  const dispatch = useDispatch();
  const { showModal, buttonLoader } = useSelector((state) => state.commonReducer);
  const { backofficeTokenDetails } = useSelector((state) => state.authReducer);

  const open = showModal?.show && showModal?.method === "EPM";
  const row = showModal?.data;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (!open) return;
    reset(
      row
        ? {
            paper_no: row?.paper_no ?? 1,
            paperlabel: row?.paperlabel ?? "",
            papername: row?.papername ?? "",
            examdate: row?.examdate ?? "",
            reportingtime: row?.reportingtime ?? "",
            gateclosingtime: row?.gateclosingtime ?? "",
            starttime: row?.starttime ?? "",
            endtime: row?.endtime ?? "",
            marks: row?.marks ?? "",
            duration: row?.duration ?? "",
          }
        : defaultValues
    );
  }, [open, row, reset]);

  const startTime = watch("starttime");
  const endTime = watch("endtime");
  const isBreakColumn = Number(watch("paper_no")) === 0;

  // the grid's duration column is the exam window itself, so it is filled in from
  // the times — still editable, a paper can be timed differently from its window
  useEffect(() => {
    if (!open || !startTime || !endTime) return;
    const minutes = toMinutes(endTime) - toMinutes(startTime);
    if (minutes > 0) setValue("duration", minutes);
  }, [open, startTime, endTime, setValue]);

  const handleClose = () => {
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: null, data: null },
    });
  };

  const onSubmit = (form) => {
    // the whole row goes on every write — the API writes omitted fields as NULL
    const data = {
      courseid: Number(courseid),
      row_type: "PAPER",
      paper_no: Number(form?.paper_no),
      paperlabel: form?.paperlabel?.trim() || null,
      papername: form?.papername?.trim(),
      examdate: form?.examdate || null,
      reportingtime: form?.reportingtime || null,
      gateclosingtime: form?.gateclosingtime || null,
      starttime: form?.starttime || null,
      endtime: form?.endtime || null,
      marks: form?.marks === "" ? null : Number(form?.marks),
      duration: form?.duration === "" ? null : Number(form?.duration),
      userid: Number(backofficeTokenDetails?.id),
    };

    dispatch(
      row
        ? {
            type: examScheduleActions.UPDATE_SCHEDULE_ROW,
            payload: { id: row?.id, data, handleClose },
          }
        : {
            type: examScheduleActions.CREATE_SCHEDULE_ROW,
            payload: { data, handleClose },
          }
    );
  };

  return (
    <Modal open={!!open} aria-labelledby="paper-row-modal">
      <Box sx={style}>
        <Typography
          variant="h5"
          sx={{
            color: `${cssProperties?.color?.primary}`,
            fontWeight: "500",
            marginBottom: "18px",
          }}
        >
          {row ? "Edit Paper" : "Add Paper"}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={
                <span>
                  Paper No
                  <span className="error" style={{ color: "red" }}> *</span>
                </span>
              }
              InputLabelProps={{ shrink: true }}
              {...register("paper_no", {
                required: "Paper no is required",
                min: { value: 0, message: "Cannot be negative" },
              })}
              error={!!errors?.paper_no}
              helperText={errors?.paper_no?.message ?? "0 = break column"}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Paper Label"
              placeholder="Paper I"
              InputLabelProps={{ shrink: true }}
              {...register("paperlabel")}
              disabled={isBreakColumn}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={
                <span>
                  {isBreakColumn ? "Break Name" : "Paper Name"}
                  <span className="error" style={{ color: "red" }}> *</span>
                </span>
              }
              placeholder={isBreakColumn ? "Lunch Break" : "General Knowledge"}
              InputLabelProps={{ shrink: true }}
              {...register("papername", {
                required: isBreakColumn
                  ? "Break name is required"
                  : "Paper name is required",
              })}
              error={!!errors?.papername}
              helperText={errors?.papername?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Exam Date"
              InputLabelProps={{ shrink: true }}
              {...register("examdate")}
              disabled={isBreakColumn}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Reporting Time"
              InputLabelProps={{ shrink: true }}
              {...register("reportingtime")}
              disabled={isBreakColumn}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Gate Closing Time"
              InputLabelProps={{ shrink: true }}
              {...register("gateclosingtime", {
                validate: (value) => {
                  const reporting = getValues("reportingtime");
                  if (!value || !reporting) return true;
                  return (
                    toMinutes(value) > toMinutes(reporting) ||
                    "Gate closing must be after reporting time"
                  );
                },
              })}
              error={!!errors?.gateclosingtime}
              helperText={errors?.gateclosingtime?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isBreakColumn}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Start Time"
              InputLabelProps={{ shrink: true }}
              {...register("starttime", {
                validate: (value) => {
                  const gateClosing = getValues("gateclosingtime");
                  if (!value || !gateClosing) return true;
                  return (
                    toMinutes(value) > toMinutes(gateClosing) ||
                    "Start time must be after gate closing"
                  );
                },
              })}
              error={!!errors?.starttime}
              helperText={errors?.starttime?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="End Time"
              InputLabelProps={{ shrink: true }}
              {...register("endtime", {
                validate: (value) => {
                  const start = getValues("starttime");
                  if (!value || !start) return true;
                  return (
                    toMinutes(value) > toMinutes(start) ||
                    "End time must be after start time"
                  );
                },
              })}
              error={!!errors?.endtime}
              helperText={errors?.endtime?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Marks"
              InputLabelProps={{ shrink: true }}
              {...register("marks")}
              disabled={isBreakColumn}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Duration (minutes)"
              InputLabelProps={{ shrink: true }}
              {...register("duration")}
              helperText="90 prints as 1 Hr. 30 Minutes"
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isBreakColumn}
            />
          </Grid>
        </Grid>

        {isBreakColumn && (
          <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 2 }}>
            Paper no 0 is the break column — only its name and its start / end
            times are printed.
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "32px" }}>
          <CancelButton handleBack={handleClose} />
          <BluePrimaryButton
            title={buttonLoader ? <ButtonLoader /> : "Submit"}
            handleSubmit={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Modal>
  );
}
